import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { fecha, hoy } from '../lib/format';
import type { BitacoraLote } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { FilterBar } from '../components/FilterBar';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { MobileFormSheet } from '../components/MobileFormSheet';

type RespuestaBitacoras = { items: BitacoraLote[]; total: number };

export function BitacoraPage() {
  const { tiene } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [abrirNueva, setAbrirNueva] = useState(false);

  const q = useQuery({
    queryKey: ['bitacoras', desde, hasta],
    queryFn: () => {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      params.set('limit', '100');
      return API.get<RespuestaBitacoras>(`/bitacora?${params.toString()}`);
    },
    enabled: tiene('bitacora:read'),
  });

  const del = useMutation({
    mutationFn: (id: string) => API.del(`/bitacora/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bitacoras'] }),
    onError: (err: unknown) => {
      const e = err as { message?: string };
      alert(`No se pudo eliminar: ${e.message ?? 'error desconocido'}`);
    },
  });

  const activeFilters = (desde ? 1 : 0) + (hasta ? 1 : 0);

  const columns: Column<BitacoraLote>[] = [
    { key: 'fecha', header: 'Fecha', render: (b) => fecha(b.fecha), mobile: 'primary' },
    {
      key: 'titulo',
      header: 'Título',
      render: (b) => b.titulo || <span className="text-slate-400">—</span>,
      mobile: 'secondary',
    },
    {
      key: 'objetivo',
      header: 'Objetivo',
      render: (b) =>
        b.objetivoPrueba ? (
          <span className="line-clamp-1">{b.objetivoPrueba}</span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    { key: 'fases', header: 'Fases', render: (b) => b.fases.length, align: 'right' },
    { key: 'evid', header: 'Fotos', render: (b) => b.evidencias.length, align: 'right' },
    {
      key: 'lote',
      header: 'Lote',
      render: (b) =>
        b.loteId ? (
          <span className="text-emerald-700 text-xs">vinculado</span>
        ) : (
          <span className="text-slate-400 text-xs">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Bitácora de proceso"
        subtitle="Registra cada día de producción: fases, tiempos, personal, resultados y evidencia fotográfica."
        action={
          tiene('bitacora:write') && (
            <button type="button" className="btn-primary" onClick={() => setAbrirNueva(true)}>
              + Nueva bitácora
            </button>
          )
        }
      />

      <div className="card p-3 sm:p-4 space-y-3">
        <FilterBar activeCount={activeFilters} title="Rango de fechas">
          <div>
            <label className="label">Desde</label>
            <input type="date" className="input" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div>
            <label className="label">Hasta</label>
            <input type="date" className="input" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </div>
          {(desde || hasta) && (
            <div className="sm:self-end">
              <button
                type="button"
                className="btn-secondary w-full sm:w-auto"
                onClick={() => {
                  setDesde('');
                  setHasta('');
                }}
              >
                Limpiar
              </button>
            </div>
          )}
        </FilterBar>

        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>
            <b>{q.data?.total ?? 0}</b> bitácoras
          </span>
          {tiene('bitacora:write') && (
            <button
              type="button"
              className="btn-primary md:hidden"
              onClick={() => setAbrirNueva(true)}
            >
              + Nueva
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <ResponsiveTable<BitacoraLote>
          columns={columns}
          rows={q.data?.items}
          rowKey={(b) => b.id}
          loading={q.isLoading}
          empty="Sin bitácoras. Crea la primera con el botón de arriba."
          actions={(b) => (
            <>
              <Link to={`/bitacora/${b.id}`} className="btn-secondary text-xs">
                Abrir
              </Link>
              {tiene('bitacora:write') && (
                <button
                  type="button"
                  className="btn-danger text-xs"
                  onClick={() => {
                    if (confirm(`¿Eliminar bitácora del ${fecha(b.fecha)}? Se borrarán también sus fotos.`))
                      del.mutate(b.id);
                  }}
                >
                  Eliminar
                </button>
              )}
            </>
          )}
        />
      </div>

      <MobileFormSheet
        open={abrirNueva}
        onClose={() => setAbrirNueva(false)}
        title="Nueva bitácora"
        desktopModal
      >
        <FormularioNueva
          onCreada={(id) => {
            setAbrirNueva(false);
            qc.invalidateQueries({ queryKey: ['bitacoras'] });
            navigate(`/bitacora/${id}`);
          }}
        />
      </MobileFormSheet>
    </div>
  );
}

function FormularioNueva({ onCreada }: { onCreada: (id: string) => void }) {
  const [fechaVal, setFechaVal] = useState(hoy());
  const [titulo, setTitulo] = useState('');
  const [objetivo, setObjetivo] = useState('');

  const crear = useMutation({
    mutationFn: () =>
      API.post<BitacoraLote>('/bitacora', {
        fecha: fechaVal,
        titulo: titulo || null,
        objetivoPrueba: objetivo || null,
      }),
    onSuccess: (b) => onCreada(b.id),
    onError: (err: unknown) => {
      const e = err as { message?: string };
      alert(`No se pudo crear: ${e.message ?? 'error'}`);
    },
  });

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        crear.mutate();
      }}
    >
      <div>
        <label className="label">Fecha</label>
        <input
          type="date"
          className="input"
          value={fechaVal}
          onChange={(e) => setFechaVal(e.target.value)}
          required
        />
      </div>
      <div>
        <label className="label">Título (opcional)</label>
        <input
          type="text"
          className="input"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="ej. Prueba maíz remojado 2h"
        />
      </div>
      <div>
        <label className="label">Objetivo / hipótesis de la prueba (opcional)</label>
        <textarea
          className="input"
          rows={3}
          value={objetivo}
          onChange={(e) => setObjetivo(e.target.value)}
          placeholder="¿Qué quieres validar hoy?"
        />
      </div>
      <button type="submit" className="btn-primary w-full" disabled={crear.isPending}>
        {crear.isPending ? 'Creando…' : 'Crear bitácora'}
      </button>
    </form>
  );
}
